import {
  Heading,
  Button,
  Select,
  Input,
  ProgressBar,
  ErrorAlert,
} from '@mm/components';
import { ENTER_KEY_NAME } from '@mm/constants/keys-codes';
import { ErrorReportingUtils } from '@mm/plugins';
import { useChatbotCRUDActions } from '@mm/state/actions/ChatbotCRUDActions';
import {
  isChatWidgetTroubleshootingError,
  useChatWidgetTroubleshootingActions,
} from '@mm/state/actions/ChatWidgetTroubleshootingActions';
import { ChatWidgetTroubleshootingToolIssueType } from '@mm/state/interfaces/chatWidgetTroubleshooting';
import { PromoterModel } from '@mm/state/model/PromoterModel';
import { domainSelector } from '@mm/state/selectors/domainSelector';
import { activeFacebookPageSelector } from '@mm/state/selectors/facebookPageSelector';
import { activeChatbotIdSelector } from '@mm/state/selectors/viewSelectors';
import classNames from 'classnames';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import './ChatWidgetTroubleshootingTool.pcss';
import {
  ChatWidgetTroubleshootingToolResult,
  ChatWidgetTroubleshootingToolResultTypes,
} from './ChatWidgetTroubleshootingToolResult/ChatWidgetTroubleshootingToolResult';
import validator from 'validator';

const IssueTypeOptions: {
  id: ChatWidgetTroubleshootingToolIssueType;
  label: string;
}[] = [
  { id: 'missing', label: 'Chat widget is not showing up' },
  { id: 'outdated', label: 'Widget behavior does not match settings' },
];

type Props = {
  promoter?: PromoterModel;
  usedInSlideout?: boolean;
  isXRay?: boolean;
  buttonName?: string;
  withHeading?: boolean;
  css?: {
    container?: string;
    textInput?: string;
    button?: string;
    diagnosticContainer?: string;
  };
  onDiagnosticEnd?: () => void;
  remotePageId?: string;
  botIdOverride?: number;
  onAfterButtonClick?: () => void;
  onClose?: () => void;
  canCloseResults?: boolean;
  showOkButton?: boolean;
  ignoreList?: ChatWidgetTroubleshootingToolResultTypes[];
  hideContactHelp?: boolean;
};

const handleUrl = (inputStr: string = '') => {
  const url = inputStr.trim();

  const isUrl = validator.isURL(url, {
    require_protocol: false,
  });

  if (isUrl && !url.includes('http')) {
    return `https://${url}`;
  }

  return url;
};

export const ChatWidgetTroubleshootingTool = (props: Props) => {
  const {
    promoter,
    usedInSlideout,
    isXRay,
    withHeading = true,
    css,
    buttonName,
    remotePageId,
    botIdOverride,
    onAfterButtonClick,
    canCloseResults,
    showOkButton,
    onClose,
    ignoreList,
    hideContactHelp,
    onDiagnosticEnd,
  } = props;
  const domains = useSelector(domainSelector);
  const firstNonFEDomain =
    domains.domainsList?.find(
      d => d.indexOf(window.location.hostname) === -1,
    ) || '';
  const activeFacebookPage = useSelector(activeFacebookPageSelector);
  const activeBotId = useSelector(activeChatbotIdSelector);
  const botId = botIdOverride ? botIdOverride : activeBotId;

  const chatWidgetTroubleshootingActions =
    useChatWidgetTroubleshootingActions();
  const chatbotActions = useChatbotCRUDActions();
  const dispatch = useDispatch();

  const [issueType, setIssueType] =
    useState<ChatWidgetTroubleshootingToolIssueType>('missing');
  const [urlState, setUrlState] = useState(firstNonFEDomain);
  const fbPageId = activeFacebookPage?.remoteId || remotePageId || '';

  const [diagnosticProgress, setDiagnosticProgress] = useState(0);
  const [diagnosticError, setDiagnosticError] =
    useState<React.ReactNode>(undefined);

  useEffect(
    () => () => {
      chatWidgetTroubleshootingActions.clear();
    },
    [chatWidgetTroubleshootingActions],
  );

  const runDiagnostic = async (
    updatedPromoter?: PromoterModel,
    attempt: number = 0,
  ) => {
    const url = handleUrl(urlState);

    if (url !== urlState) {
      setUrlState(url);
    }

    setIsResultsVisible(true);
    setDiagnosticError(undefined);

    if (!attempt) {
      setDiagnosticProgress(0.01);
      chatWidgetTroubleshootingActions.clear();
    }
    const progressBarUpdateInterval = setInterval(
      () => setDiagnosticProgress(p => Math.min(0.97, p + 0.03)),
      1000,
    );

    try {
      await chatbotActions.updateGlobalJS(botId);
      await chatWidgetTroubleshootingActions.run(
        url,
        fbPageId,
        issueType,
        botId,
        updatedPromoter || promoter,
      );
      onDiagnosticEnd?.();
    } catch (err) {
      if (isChatWidgetTroubleshootingError(err)) {
        if (err.status === 508 && attempt <= 2) {
          clearInterval(progressBarUpdateInterval);
          await runDiagnostic(updatedPromoter || promoter, attempt + 1);
          return;
        }
        if (err.status === 422) {
          setDiagnosticError(
            <>
              An error occurred while running the diagnostic tool. Unable to
              load URL{' '}
              <a href={url} target="_blank" rel="noopener noreferrer">
                <code>
                  {url} <i className="fas fa-external-link-alt" />
                </code>
              </a>
              <br />
              Please ensure that the site is accessible to external visitors.
            </>,
          );
          return;
        }
      }
      ErrorReportingUtils.captureException(err);
      setDiagnosticError(
        'An error occurred while running the diagnostic tool.  Please try again later.',
      );
    } finally {
      clearInterval(progressBarUpdateInterval);
      setDiagnosticProgress(0);
      onAfterButtonClick?.();
    }
  };

  const [isResultsVisible, setIsResultsVisible] = useState(true);

  const onInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!diagnosticProgress && e.key === ENTER_KEY_NAME) {
      runDiagnostic();
    }
  };

  const handleClose = () => {
    setIsResultsVisible(false);
    onClose?.();
  };

  return (
    <div
      className={classNames('chat-widget-troubleshooting-tool', css?.container)}
    >
      {!usedInSlideout && (
        <Heading size={3}>Chat Widget Diagnostic Tool</Heading>
      )}
      <div className="chat-widget-troubleshooting-tool__controls-container">
        {!isXRay && (
          <>
            <Heading addMarginAbove size={usedInSlideout ? 5 : 4} isBold>
              Issue type:
            </Heading>
            <Select
              hideClearButton
              hideCount
              className="chat-widget-troubleshooting-tool__issue-selector"
              selected={[
                IssueTypeOptions.find(o => o.id === issueType) ||
                  IssueTypeOptions[0],
              ]}
              options={IssueTypeOptions}
              onChange={t => {
                if (t.length) {
                  setIssueType(t[t.length - 1].id);
                }
              }}
              disabled={!!diagnosticProgress}
            />
          </>
        )}
        {withHeading && (
          <Heading size={usedInSlideout ? 5 : 4} isBold>
            Website URL:
          </Heading>
        )}

        <Input
          type="text"
          css={{ container: css?.textInput }}
          placeholder="https://yourdomain.com"
          value={urlState}
          disabled={!!diagnosticProgress}
          onChange={e => setUrlState(e.target.value)}
          onKeyDown={onInputKeyDown}
        />
        <div className="chat-widget-troubleshooting-tool__controls-run-button-row">
          <Button
            css={{
              container: classNames(
                'chat-widget-troubleshooting-tool__run-button',
                css?.button,
              ),
              leftIcon: 'chat-widget-troubleshooting-tool__run-button-icon',
            }}
            onClick={() => runDiagnostic()}
            disabled={
              !!diagnosticProgress || !urlState.length || !fbPageId.length
            }
          >
            {buttonName || 'Run diagnostic'}
          </Button>
          {!!diagnosticProgress && (
            <ProgressBar
              caption="Diagnostic in progress"
              progress={diagnosticProgress}
            />
          )}
        </div>
      </div>
      {diagnosticError && (
        <ErrorAlert className="chat-widget-troubleshooting-tool__error-alert">
          {diagnosticError}
        </ErrorAlert>
      )}
      {isResultsVisible && (
        <ChatWidgetTroubleshootingToolResult
          ignoreList={ignoreList}
          hideContactHelp={hideContactHelp}
          canCloseResults={canCloseResults}
          onClose={handleClose}
          issueType={issueType}
          runDiagnostic={runDiagnostic}
          promoter={promoter}
          isXRay={isXRay}
          css={{ container: css?.diagnosticContainer }}
        >
          {showOkButton && onClose && <Button onClick={handleClose}>OK</Button>}
        </ChatWidgetTroubleshootingToolResult>
      )}
    </div>
  );
};
